import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip,
} from '@mui/material';
import api from '../../api/client';
import useCADWebSocket from '../../hooks/useCADWebSocket';
import { formatTime24, formatDateShort, ensureTimezone } from '../../utils/time';

interface LogEntry {
  id: number;
  timestamp: string;
  username: string;
  action: string;
  target_type: string;
  target_name: string;
  old_value: string;
  new_value: string;
  details: string;
}

const ACTION_COLORS: Record<string, string> = {
  unit_logon: '#4caf50',
  unit_logoff: '#f44336',
  unit_status_changed: '#2196f3',
  unit_created: '#00bcd4',
  unit_location_set: '#ff9800',
  unit_dispatched: '#ff5722',
  unit_cleared: '#8bc34a',
  call_created: '#e91e63',
  call_updated: '#9c27b0',
  call_merged: '#673ab7',
  user_login: '#607d8b',
  password_changed: '#795548',
};

const ACTION_LABELS: Record<string, string> = {
  unit_logon: 'Unit Logon',
  unit_logoff: 'Unit Logoff',
  unit_status_changed: 'Status Change',
  unit_created: 'Unit Created',
  unit_location_set: 'Location Set',
  unit_dispatched: 'Unit Dispatched',
  unit_cleared: 'Unit Cleared',
  call_created: 'Call Created',
  call_updated: 'Call Updated',
  call_merged: 'Call Merged',
  user_login: 'User Login',
  password_changed: 'Password Changed',
};

const DENSE_CELL = { py: 0.5, px: 1, fontSize: 13, lineHeight: 1.4 } as const;
const DENSE_HEADER = { ...DENSE_CELL, fontWeight: 700 } as const;

export default function DispatchLogPage() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [tzReady, setTzReady] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadLogs = useCallback(() => {
    api.get('/audit-logs/dispatch?limit=500')
      .then((r) => setLogs(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    ensureTimezone().then(() => setTzReady(true)).catch(() => setTzReady(true));
  }, []);

  useEffect(() => {
    if (!tzReady) return;
    loadLogs();
    intervalRef.current = setInterval(loadLogs, 10000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadLogs, tzReady]);

  const onWsEvent = useCallback(() => {
    loadLogs();
  }, [loadLogs]);

  useCADWebSocket(onWsEvent);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#1a1a2e', color: '#fff' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Dispatch Log</Typography>
        <Chip label={`${logs.length} entries`} size="small" sx={{ bgcolor: '#333', color: '#fff' }} />
      </Box>
      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={DENSE_HEADER}>Date</TableCell>
              <TableCell sx={DENSE_HEADER}>Time</TableCell>
              <TableCell sx={DENSE_HEADER}>Action</TableCell>
              <TableCell sx={DENSE_HEADER}>User</TableCell>
              <TableCell sx={DENSE_HEADER}>Target</TableCell>
              <TableCell sx={DENSE_HEADER}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell sx={{ ...DENSE_CELL, fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {formatDateShort(log.timestamp)}
                </TableCell>
                <TableCell sx={{ ...DENSE_CELL, fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {formatTime24(log.timestamp)}
                </TableCell>
                <TableCell sx={DENSE_CELL}>
                  <Chip
                    size="small"
                    label={ACTION_LABELS[log.action] || log.action}
                    sx={{
                      bgcolor: ACTION_COLORS[log.action] || '#666',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 11,
                      height: 22,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...DENSE_CELL, fontWeight: 600 }}>{log.username}</TableCell>
                <TableCell sx={{ ...DENSE_CELL, fontFamily: 'monospace' }}>
                  {log.target_name || '—'}
                </TableCell>
                <TableCell sx={DENSE_CELL}>
                  {log.old_value && log.new_value ? (
                    <>{log.old_value} → {log.new_value}{log.details ? ` (${log.details})` : ''}</>
                  ) : log.details || '—'}
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No log entries
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
